import * as Sentry from '@sentry/react';
import { push } from 'modules/router/actions';
import { AppStartListening } from 'App/ListenerMiddleware';
import { createListenerMiddleware } from '@reduxjs/toolkit';

import authApi from 'modules/auth/service';
import { getSearch, isReopenKioskPage } from 'modules/router/selectors';
import { isKioskEmergencyMode, isKioskReadyOptional, isMFAonly as isMFAonlySelector } from 'modules/dealers/selectors';

import { KeyType } from 'modules/kiosk/types/GetReadySelfServices';
import { SelfServiceType } from 'modules/selfServices/types/SelfService';

import { getRegistration, getSelfServiceType } from 'modules/selfServices/selectors';
import { ChannelEnum, MfaScopeEnum } from 'modules/auth/types/LoginRequest';
import { SelfOfficeLoginMethod } from 'modules/dealers/types/ContextResponse';

import { isAfter } from 'date-fns';
import { getDefaultChannelToSendCode, getRetryExpiration } from 'modules/communication/utils';
import { isExpectedClientError, toReportableError } from 'utils/sentryUtils';
import selfServicesApi from 'modules/selfServices/service';
import SelfServiceStatus from 'modules/selfServices/types/SelfServiceStatus';

import kioskApi from './service';
import * as actions from './actions';
import { setDoorError, setDoorStatus } from './actions';
import { CLOSE_EVENT_FALLBACK_TIMEOUT_MS } from './constants';

import {
  getKioskId,
  isKioskReady,
  getFreeSlotId,
  getSelfServiceById,
  getSlotBySelfServiceId,
  getSlotIdBySelfServiceId,
  getReadySelfServiceSearchText,
} from './selectors';

import {
  CloseKeysSafeRequest,
  CloseSafeAction,
  DoorsStatus,
  KeysSafeAction,
  OpenKeysSafeRequest,
} from './types/KeysSafe';
import { KeyStatus } from './types/Kiosk';
import { getLoginData } from 'modules/auth/selectors';

const listenerMiddleware = createListenerMiddleware();
const startAppListening = listenerMiddleware.startListening as AppStartListening;

const handleErrorWhenOpeningOrClosing = (event: unknown, errorPayload: Record<string, unknown>) => {
  // Expected 4xx business errors (e.g. "key is not in the slot") are handled by
  // the UI (door error state); don't report them to Sentry as JS errors.
  if (isExpectedClientError(event)) {
    return;
  }

  Sentry.setContext('operation', errorPayload);
  Sentry.captureException(toReportableError(event));
};

startAppListening({
  actionCreator: actions.openKeysSafe,
  effect: async ({ payload }, { dispatch, getState, take }) => {
    const isReadyOptional = isKioskReadyOptional(getState());
    const isReady = isKioskReady(getState());
    if (!isReadyOptional && !isReady) {
      await take(actions.setIsKioskReady.match);
    }

    const state = getState();
    const kioskId = getKioskId(state) as string;

    const isKioskReopen = isReopenKioskPage(state);
    const hasKioskDoorsError = isKioskEmergencyMode(state);

    const {
      slotId, selfServiceId, selfServiceType, asAdmin, emergency, keyStatus,
    } = payload as KeysSafeAction;

    const isCheckout = selfServiceType === SelfServiceType.CHECK_OUT;

    const endpointPayload: OpenKeysSafeRequest = {
      emergency,
      kioskId,
      slotId,
      selfServiceId,
      reopen: isKioskReopen,
    };

    try {
      if (emergency) {
        if (hasKioskDoorsError) {
          await dispatch(kioskApi.endpoints.openDoorsEmergency.initiate({ kioskId }, { forceRefetch: true })).unwrap();
        } else {
          await dispatch(kioskApi.endpoints.openSlotAsAdmin.initiate(endpointPayload, { forceRefetch: true })).unwrap();
        }
      } else if (asAdmin) {
        const newSlotId = getSlotIdBySelfServiceId(state, selfServiceId);
        const isMobilityCheckIn = selfServiceType === SelfServiceType.CHECK_IN && keyStatus === KeyStatus.NONE;
        endpointPayload.slotId = newSlotId;
        if ((isCheckout || isMobilityCheckIn) && !newSlotId) {
          endpointPayload.slotId = getFreeSlotId(state);
        }

        if (endpointPayload.slotId) {
          await dispatch(kioskApi.endpoints.openSlotAsAdmin.initiate(endpointPayload, { forceRefetch: true })).unwrap();
        } else {
          await dispatch(actions.setDoorStatus(DoorsStatus.CLOSED));
        }
      } else {
        await dispatch(kioskApi.endpoints.openSlot.initiate(endpointPayload, { forceRefetch: true })).unwrap();
      }
    } catch (e) {
      handleErrorWhenOpeningOrClosing(e, { ...endpointPayload, action: 'opening' });
      dispatch(setDoorError(true));
      dispatch(setDoorStatus(DoorsStatus.PENDING));
    }
  },
});

startAppListening({
  actionCreator: actions.closeKeysSafe,
  effect: async ({ payload }, { dispatch, getState, take }) => {
    const isReadyOptional = isKioskReadyOptional(getState());
    const isReady = isKioskReady(getState());
    if (!isReadyOptional && !isReady) {
      await take(actions.setIsKioskReady.match);
    }

    const state = getState();
    const kioskId = getKioskId(state) as string;

    const isKioskReopen = isReopenKioskPage(state);
    const hasKioskDoorsError = isKioskEmergencyMode(state);

    const {
      slotId, selfServiceId, selfServiceType, asAdmin, hasAccepted, emergency, keyStatus, keyType,
    } = payload as CloseSafeAction;

    const slot = getSlotBySelfServiceId(state, selfServiceId);

    const isUnFinalize = slot?.finalized === false;
    const isCheckout = selfServiceType === SelfServiceType.CHECK_OUT;
    const isMobilityCheckIn = selfServiceType === SelfServiceType.CHECK_IN && (
      keyStatus === KeyStatus.NONE || keyType === KeyType.MOBILITY
    );
    const canUnprepared = isMobilityCheckIn || (
      isCheckout && keyType === KeyType.CUSTOMER && keyStatus === KeyStatus.IN_SLOT && !isUnFinalize
    );

    const endpointPayload: CloseKeysSafeRequest = {
      kioskId,
      emergency,
      selfServiceId,
      slotId: slot?.id,
      status: isCheckout ? KeyStatus.RETRIEVED : KeyStatus.IN_SLOT,
    };

    try {
      if (emergency) {
        if (hasKioskDoorsError) {
          await dispatch(kioskApi.endpoints.closeAllSlotsAsAdmin.initiate({ kioskId })).unwrap();
        } else {
          endpointPayload.slotId = slotId;
          await dispatch(kioskApi.endpoints.closeSlotAsAdmin.initiate(
            endpointPayload,
            { forceRefetch: true },
          )).unwrap();
        }
      } else if (asAdmin) {
        if (hasAccepted) {
          const { slotId: originalSlotId } = getSelfServiceById(state, selfServiceId ?? '');

          if (canUnprepared && originalSlotId) {
            await dispatch(kioskApi.endpoints.unprepareSlotAsAdmin.initiate(endpointPayload)).unwrap();
          } else {
            // Admin logic is reversed
            const isInSlotCheckout = (isCheckout && keyType !== KeyType.MOBILITY);
            const status = !isUnFinalize && (isInSlotCheckout || isMobilityCheckIn)
              ? KeyStatus.IN_SLOT
              : KeyStatus.RETRIEVED;
            await dispatch(kioskApi.endpoints.updateSlotAsAdmin.initiate({ ...endpointPayload, status })).unwrap();
          }
        } else {
          await dispatch(kioskApi.endpoints.closeSlotAsAdmin.initiate(endpointPayload, {
            forceRefetch: true,
          })).unwrap();
        }
      } else if (isKioskReopen) {
        await dispatch(kioskApi.endpoints.closeSlot.initiate(endpointPayload)).unwrap();
      } else {
        await dispatch(kioskApi.endpoints.updateSlot.initiate(endpointPayload)).unwrap();
      }

      // adding a timeout to the take will result to null being returned after this time.
      const doorClosed = await take(
        (action) => actions.setDoorStatus.match(action) && action.payload === DoorsStatus.CLOSED,
        CLOSE_EVENT_FALLBACK_TIMEOUT_MS,
      );

      if (doorClosed === null && selfServiceId) {
        const selfService = await dispatch(
          selfServicesApi.endpoints.getSelfServiceById.initiate(
            { id: selfServiceId },
            { forceRefetch: true },
          ),
        ).unwrap();
        if (selfService.status === SelfServiceStatus.ANSWERED) {
          dispatch(actions.setDoorStatus(DoorsStatus.CLOSED));
        }
      }

      if (asAdmin) {
        if (!emergency) {
          const text = getReadySelfServiceSearchText(getState());
          await dispatch(kioskApi.endpoints.getReadySelfServices.initiate(
            { kioskId, type: selfServiceType, text },
            { forceRefetch: true },
          )).unwrap();
        }
        await dispatch(kioskApi.endpoints.getKioskData.initiate({ kioskId }, { forceRefetch: true })).unwrap();
      }
    } catch (e) {
      handleErrorWhenOpeningOrClosing(e, { ...endpointPayload, action: 'closing' });
    }
  },
});

// Redirect to the home page if the doors are closed on emergency mode
// Context will be fetched once on this page by `src/modules/app/middleware.ts`
startAppListening({
  predicate: (action, state) => Boolean(
    isKioskEmergencyMode(state)
    && setDoorStatus.match(action)
    && (action.meta.origin === DoorsStatus.CLOSED || action.payload === DoorsStatus.CLOSED),
  ),
  effect: async (_, { dispatch, getState }) => {
    const search = getSearch(getState());
    dispatch(push(`/${search}`));
  },
});

startAppListening({
  actionCreator: actions.reopenKiosk,
  effect: async (_, { dispatch, getState }) => {
    const type = getSelfServiceType(getState());
    const registrationFromService = getRegistration(getState()) as string;
    const { registration : reg } = getLoginData(getState());
    const registration = reg ?? registrationFromService;
    const isBreakdownSelfService = type === SelfServiceType.BREAKDOWN;
    // retro compatibility for MFA only.
    // nothing change if we are in only MFA.
    const isMFAonly = isMFAonlySelector(getState());
    console.log('[REOPEN][kiosk/middleware] reopenKiosk triggered', { type, registration, isMFAonly, isBreakdownSelfService });
    if (isMFAonly || isBreakdownSelfService) {
      const data = await dispatch(authApi.endpoints.getMFAConnection.initiate({
        type,
        value: registration,
        searchType: SelfOfficeLoginMethod.REGISTRATION,
        scope: MfaScopeEnum.REOPEN,
      })).unwrap();

      const channel = type === SelfServiceType.BREAKDOWN ? ChannelEnum.SMS : getDefaultChannelToSendCode(data);
      const { customerType } = data || {};
      const retryExpiration = getRetryExpiration(data, channel);

      console.log('[REOPEN][kiosk/middleware] getMFAConnection done, sending MFA code via', channel);
      if (!retryExpiration || isAfter(new Date(), new Date(retryExpiration))) {
        const { connectionId, selfServiceId } = data;
        await dispatch(authApi.endpoints.sendMFACode.initiate({
          channel,
          customerType,
          connectionId,
          selfServiceId,
        }));
      }
    }

    const search = getSearch(getState());
    console.log('[REOPEN][kiosk/middleware] navigating to /reopen', search);
    dispatch(push(`/reopen${search}`));
  },
});

export default listenerMiddleware;
